import { Dialog } from '@/components/Dialog';
import Divider from '@/components/Divider';
import { UploadIcon } from '@/components/Icons';
import Show from '@/components/Show';
import { Textarea } from '@/components/ui/textarea';
import PromptInputContainer from '@/pages/Home/Generator/components/SimpleEffects/SimplePromptInput/PromptInputContainer';
import useGeneratorStore from '@/state/generator-store';
import { cn } from '@/utils';
import React, { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import MobileStyleFilter from '../../NewStyleFilter/MobileStyleFilter';
import MobileDetailEffects from './MobileDetailEffects';
import MobileGeneratorButton from './MobileGeneratorButton';
import { useMobileTextareaStore } from '../../../hooks/useMobileTextareaStore';
import { useController } from 'react-hook-form';

const MobileSimpleEffects: React.FC = () => {
  const { register, isGeneratorDrawerShow, setIsGeneratorDrawerShow, control } = useGeneratorStore((state) => ({
    register: state.register,
    isGeneratorDrawerShow: state.isGeneratorDrawerShow,
    setIsGeneratorDrawerShow: state.setIsGeneratorDrawerShow,
    control: state.control,
  }));

  const location = useLocation();
  const [searchParams] = useSearchParams();

  const dialogRootId = 'mobile-dialog-root';
  const dialogRootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setIsGeneratorDrawerShow(false);
  }, [location]);

  const openMobileStyleFilterDialog = () => {
    const container = dialogRootRef.current ?? document.body;
    Dialog.info({
      closeClassName: 'hidden',
      dialogContentClassName: 'bg-[transparent] w-[348px]  lg:max-w-[348px]',
      contentJSX: ({ onClose }) => {
        return <MobileStyleFilter close={onClose} />;
      },
      container,
    });
  };

  const {
    field: { value: styleFilterId },
  } = useController({
    name: 'styleFilterId',
    control,
  });

  const { textareaRef } = useMobileTextareaStore();

  useEffect(() => {
    if (styleFilterId && textareaRef.current) {
      textareaRef.current?.focus();
    }
  }, [textareaRef.current, styleFilterId]);

  useEffect(() => {
    const sfAutoOpen = searchParams.get('sfAutoOpen') === 'true';
    if (sfAutoOpen) {
      openMobileStyleFilterDialog();
    }
  }, [location]);

  return (
    <>
      <div ref={dialogRootRef} id={dialogRootId} />
      <Show when={!isGeneratorDrawerShow}>
        <div className={cn('w-full inline-flex bg-[#212324] h-24 py-3 px-4 rounded-t-lg text-white text-lg relative')}>
          <PromptInputContainer lineCount={1} className="flex-grow h-16">
            <>
              <div className="flex items-center text-sm">
                <UploadIcon className="w-4 h-4" />
                <Divider border={{ direction: 'right', color: '#121213' }} height="1.5rem" />
              </div>
              <div className="w-full">
                <Textarea
                  className="min-h-[60px] w-full h-full bg-transparent border-none text-[16px] font-normal leading-[1.5] text-left text-white placeholder-content-tertiary placeholder:text-slate-400/75 placeholder:pt-[4px] focus:outline-none resize-none overflow-y-auto break-words relative"
                  placeholder={isGeneratorDrawerShow ? '' : `Imagine "Rock and Roll music..."`}
                  style={{
                    boxShadow: 'none',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                  }}
                  {...register('prompt')}
                  autoCorrect="off"
                  autoComplete="off"
                  autoCapitalize="off"
                  onFocus={(e) => {
                    setIsGeneratorDrawerShow(true);
                  }}
                  onBlur={(e) => {
                    setIsGeneratorDrawerShow(false);
                  }}
                  spellCheck={false}
                />
              </div>
            </>
          </PromptInputContainer>
        </div>
      </Show>
      <Show when={isGeneratorDrawerShow}>
        <div className="w-full">
          <MobileDetailEffects openMobileStyleFilterDialog={openMobileStyleFilterDialog} />
          <MobileGeneratorButton />
        </div>
      </Show>
    </>
  );
};

export default MobileSimpleEffects;
